import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { Empty } from "antd";
import { ArrowRightOutlined, FileImageOutlined, FileTextOutlined } from "@ant-design/icons";

import { GalleryOrgan, useGalleryOrgans, useOcspstGalleryCard } from "../../hooks/useGalleryOrgans";
import { OCSPST_CATEGORY } from "../category/ocspstCategory";
import { MainColor, SubColor } from "../../style/styleValue";

interface Props {
  /** 맨 위에 붙일 내용 (시안 검토 페이지의 GalleryDraftBar 등) */
  header?: ReactNode;
}

/**
 * /gallery 화면 — 디자인 시안 A(썸네일 카드) 채택안.
 * organ 별 대표 썸네일을 큰 커버 카드로 보여주고, 누르면 해당 organ 갤러리(/post-list/:id)로 이동한다.
 * 로그인·권한 확인은 이 컴포넌트를 쓰는 페이지(Main)에서 한다.
 */
const GalleryCover = ({ header }: Props) => {
  const navigate = useNavigate();
  const { organs, loading, error } = useGalleryOrgans();
  const ocspstCard = useOcspstGalleryCard();

  const totalPosts = organs.reduce((sum, o) => sum + o.postCnt, 0);
  const totalSlides = organs.reduce((sum, o) => sum + o.slideCnt, 0);

  return (
    <Page>
      {header}

      <Hero>
        <Eyebrow>ATLAS GALLERY</Eyebrow>
        <HeroTitle>Digital Slide Archive</HeroTitle>
        {/* <HeroSub>보고 싶은 장기를 고르면 해당 organ 의 전체 갤러리로 이동합니다.</HeroSub> */}
        <HeroStats>
          {/* <span>
            <b>{organs.length}</b> organs
          </span>
          <Divider /> */}
          <span>
            <b>{totalPosts.toLocaleString()}</b> posts
          </span>
          <Divider />
          <span>
            <b>{totalSlides.toLocaleString()}</b> slides
          </span>
        </HeroStats>
      </Hero>

      {error ? (
        <CenterState>
          <Empty description="갤러리 목록을 불러오지 못했습니다." />
        </CenterState>
      ) : loading ? (
        <Grid>
          {Array.from({ length: 8 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </Grid>
      ) : organs.length === 0 ? (
        <CenterState>
          <Empty description="표시할 organ 이 없습니다." />
        </CenterState>
      ) : (
        <Grid>
          {organs.map((organ) => (
            <CoverCard key={organ.id} organ={organ} onClick={() => navigate(`/post-list/${organ.id}`)} />
          ))}
          {ocspstCard && (
            <CoverCard organ={ocspstCard} onClick={() => navigate(`/post-list/${OCSPST_CATEGORY.key}`)} />
          )}
        </Grid>
      )}
    </Page>
  );
};

const CoverCard = ({ organ, onClick }: { organ: GalleryOrgan; onClick: () => void }) => {
  const cover = organ.thumbs[0];

  return (
    <Card onClick={onClick} title={organ.name}>
      <Cover>
        {cover ? (
          <img
            src={cover}
            alt={organ.name}
            loading="lazy"
            onError={(e) => {
              const el = e.currentTarget;
              el.style.display = "none";
              (el.parentElement as HTMLElement).classList.add("broken");
            }}
          />
        ) : organ.thumbsLoading ? (
          <Shimmer />
        ) : (
          <NoImage>{organ.name.slice(0, 2).toUpperCase()}</NoImage>
        )}
        <Scrim />
        <CoverBody>
          <OrganName>{organ.name}</OrganName>
          <Counts>
            <span>
              <FileTextOutlined /> {organ.postCnt}
            </span>
            <span>
              <FileImageOutlined /> {organ.slideCnt}
            </span>
          </Counts>
        </CoverBody>
        <GoBadge>
          갤러리 보기 <ArrowRightOutlined />
        </GoBadge>
      </Cover>

      {organ.subNames.length > 0 && (
        <SubLine title={organ.subNames.join(", ")}>{organ.subNames.join(" · ")}</SubLine>
      )}
    </Card>
  );
};

const shimmer = keyframes`
  0% { background-position: -320px 0; }
  100% { background-position: 320px 0; }
`;

const Page = styled.div`
  width: 100%;
  padding-bottom: 5rem;
  background: #fff;
`;

const Hero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 3.5rem 2rem 2.75rem;
`;

const Eyebrow = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  color: ${SubColor};
  margin-bottom: 14px;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: 2.6rem;
  font-weight: 700;
  letter-spacing: -0.6px;
  color: ${MainColor};

  @media (max-width: 768px) {
    font-size: 1.9rem;
  }
`;

const HeroSub = styled.p`
  margin: 14px 0 0;
  font-size: 0.95rem;
  color: #8a94a3;
`;

const HeroStats = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 22px;
  font-size: 0.85rem;
  color: #667085;

  b {
    color: ${MainColor};
    font-size: 1rem;
  }
`;

const Divider = styled.span`
  width: 1px;
  height: 12px;
  background: #dbe2ea;
`;

const CenterState = styled.div`
  min-height: 40vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 26px;
  padding: 0 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 1.25rem;
    gap: 18px;
  }
`;

const Cover = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 14px;
  background: #eef1f5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  &.broken {
    background: repeating-linear-gradient(45deg, #f0f2f5, #f0f2f5 12px, #e8ebf0 12px, #e8ebf0 24px);
  }
`;

const Card = styled.article`
  cursor: pointer;

  &:hover ${Cover} img {
    transform: scale(1.06);
  }
`;

const Scrim = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(10, 24, 42, 0.82) 0%, rgba(10, 24, 42, 0.25) 42%, rgba(10, 24, 42, 0.05) 70%);
`;

const CoverBody = styled.div`
  position: absolute;
  left: 20px;
  right: 20px;
  bottom: 18px;
  color: #fff;
`;

const OrganName = styled.h2`
  margin: 0 0 6px;
  font-size: 1.55rem;
  font-weight: 700;
  letter-spacing: -0.3px;
  color: #fff;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Counts = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.88);

  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
`;

const GoBadge = styled.span`
  position: absolute;
  top: 16px;
  right: 16px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: ${MainColor};
  font-size: 0.75rem;
  font-weight: 700;
  opacity: 0;
  transform: translateY(-4px);
  transition: opacity 0.25s ease, transform 0.25s ease;

  ${Card}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SubLine = styled.p`
  margin: 12px 2px 0;
  font-size: 0.78rem;
  color: #98a2b3;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Shimmer = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #eef1f5 25%, #f7f9fb 50%, #eef1f5 75%);
  background-size: 640px 100%;
  animation: ${shimmer} 1.3s linear infinite;
`;

const NoImage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.4rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #c6d0dc;
  background: linear-gradient(135deg, #f5f7fa 0%, #e9eef4 100%);
`;

const SkeletonCard = styled.div`
  aspect-ratio: 4 / 3;
  border-radius: 14px;
  background: linear-gradient(90deg, #eef1f5 25%, #f7f9fb 50%, #eef1f5 75%);
  background-size: 640px 100%;
  animation: ${shimmer} 1.3s linear infinite;
`;

export default GalleryCover;
